import {useOptionalCommentEditsContext} from '@github-ui/commenting/CommentEditsContext'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {ConfirmationDialog} from '@primer/react'
import {useCallback} from 'react'

import {ERRORS} from '../../constants/errors'
import {MESSAGES} from '../../constants/messages'
import {useDeleteIssueMutation} from '../../mutations/delete-issue-mutation'
import type {IssueDeletionConnection, OptionConfig} from '../OptionConfig'
import styles from './IssueDeletionConfirmationDialog.module.css'

type IssueDeletionConfirmationDialogProps = {
  issueId: string
  owner: string
  repository: string
  number: number
  issueDeletionConnection?: IssueDeletionConnection
  onSuccessfulDeletion?: OptionConfig['onIssueDelete']
  afterDelete?: () => void
  onClose: () => void
}

export const IssueDeletionConfirmationDialog = ({
  issueId,
  owner,
  repository,
  number,
  issueDeletionConnection,
  onSuccessfulDeletion,
  afterDelete,
  onClose,
}: IssueDeletionConfirmationDialogProps) => {
  const commentEdits = useOptionalCommentEditsContext()
  const {addToast} = useToastContext()
  const onError = useCallback(() => {
    // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration -- The confirmation dialog is already unmounted when a delayed DELETE fails.
    addToast({type: 'error', message: ERRORS.couldNotDeleteIssue, role: 'alert'})
  }, [addToast])
  const onCompleted = useCallback(
    async (deletedIssueId: string) => {
      await onSuccessfulDeletion?.(deletedIssueId, {alreadySettled: true})
      // Drafts on a deleted issue can no longer be saved, and leaving them active makes the
      // `beforeunload` guard prompt when `afterDelete` lands outside the app -- cancelling that
      // prompt strands the user on an issue that is already gone.
      commentEdits?.cancelAllCommentEdits()
      afterDelete?.()
    },
    [afterDelete, commentEdits, onSuccessfulDeletion],
  )
  const deleteIssueMutation = useDeleteIssueMutation({
    owner,
    repository,
    number,
    issueId,
    issueDeletionConnection,
    onCompleted,
    onError,
  })

  const onDialogClose = useCallback(
    (gesture: 'confirm' | 'cancel' | 'close-button' | 'escape') => {
      if (gesture === 'confirm') {
        deleteIssueMutation.mutate({
          operation: {
            type: 'removeFromConnection',
            connectionPath: issueDeletionConnection?.connectionPath ?? [],
            nodeId: issueId,
          },
        })
      }
      onClose()
    },
    [deleteIssueMutation, issueDeletionConnection?.connectionPath, issueId, onClose],
  )

  return (
    <ConfirmationDialog
      title={MESSAGES.deleteIssueConfirmationTitle}
      confirmButtonContent={MESSAGES.deleteIssueConfirmationButton}
      confirmButtonType="danger"
      onClose={onDialogClose}
    >
      <div className={styles.confirmationContent}>
        <ul className={styles.confirmationList}>
          {MESSAGES.deleteIssueConfirmationActionPoints.map(actionPoint => (
            <li key={actionPoint}>
              <span>{actionPoint}</span>
            </li>
          ))}
        </ul>
      </div>
    </ConfirmationDialog>
  )
}
